import React, { useState } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { MenuSectionBreakdown } from "@/redux/services/dashboard/dashboard.type";
import { cn, formatPrice } from "@/lib/utils";
import { getGrowthIndicator } from "@/lib/growthIndicator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  dashboardColors,
  dashboardShadows,
  dashboardSpacing,
  chartColorPalette,
} from "@/lib/theme";
import { useRestaurantCurrency } from "@/lib/hooks";

interface SectionBreakdownChartProps {
  data: MenuSectionBreakdown[];
  previousData?: MenuSectionBreakdown[];
  loading?: boolean;
  /** Pie canvas height in px. Defaults to 350 (dashboard size). */
  height?: number;
  /**
   * Grid classes for the pie/legend split. Defaults to a 50/50 at xl (the
   * dashboard uses this inside a half-width card). Full-width hosts like the
   * Rapports page pass a 70/30 split from lg up.
   */
  gridClassName?: string;
}

export const SectionBreakdownChart: React.FC<SectionBreakdownChartProps> = ({
  data,
  previousData,
  loading,
  height = 350,
  gridClassName = "grid grid-cols-1 xl:grid-cols-2 gap-4",
}) => {
  const currency = useRestaurantCurrency();
  const [metric, setMetric] = useState<"orders" | "revenue">("revenue");

  if (loading) {
    return (
      <div
        className="rounded-lg"
        style={{
          backgroundColor: dashboardColors.bgCard,
          padding: dashboardSpacing.cardPadding,
          boxShadow: dashboardShadows.card,
        }}
      >
        <Skeleton className="h-6 w-48 mb-4" />
        <Skeleton className="h-[450px] w-full" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div
        className="rounded-lg"
        style={{
          backgroundColor: dashboardColors.bgCard,
          padding: dashboardSpacing.cardPadding,
          boxShadow: dashboardShadows.card,
        }}
      >
        <h3
          className="text-lg font-semibold mb-4"
          style={{ color: dashboardColors.textPrimary }}
        >
          Répartition par section
        </h3>
        <div className="flex items-center justify-center h-[400px]">
          <p style={{ color: dashboardColors.textSecondary }}>
            Aucune donnée disponible
          </p>
        </div>
      </div>
    );
  }

  // Size, sort and share are all driven by the selected metric (revenue or
  // number of orders), computed client-side so the pie, its data labels, the
  // tooltip and the legend stay consistent with what's chosen. The server
  // `percentage` (an item-occurrence share) is intentionally not used — it
  // contradicted the revenue framing and produced the reported data errors
  // (SFRESTO-87).
  const total = data.reduce((sum, item) => sum + item[metric], 0);
  const sortedData = [...data]
    .sort((a, b) => b[metric] - a[metric])
    .map((item) => ({
      ...item,
      section: item.section,
      share: total > 0 ? (item[metric] / total) * 100 : 0,
    }));

  const series = sortedData.map((item) => item[metric]);
  const labels = sortedData.map((item) => item.section);

  const legendTitle =
    metric === "revenue"
      ? "Top sections par revenus"
      : "Top sections par quantité vendue";
  const formatMetric = (section: (typeof sortedData)[number]) =>
    metric === "revenue"
      ? formatPrice(section.revenue / 100, currency)
      : `${section.orders}`;

  const options: ApexOptions = {
    chart: {
      type: "pie",
      height,
      fontFamily: "Inter, sans-serif",
    },
    labels: labels,
    colors: chartColorPalette.slice(0, sortedData.length),
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: true,
      formatter: function (_val: number, opts: any) {
        const share = sortedData[opts.seriesIndex]?.share ?? 0;
        return share.toFixed(0) + "%";
      },
      style: {
        fontSize: "14px",
        fontFamily: "Inter, sans-serif",
        fontWeight: "600",
        colors: ["#fff"],
      },
      dropShadow: {
        enabled: false,
      },
    },
    plotOptions: {
      pie: {
        dataLabels: {
          offset: -5,
          minAngleToShowLabel: 10,
        },
      },
    },
    stroke: {
      width: 2,
      colors: ["#fff"],
    },
    fill: {
      opacity: 1,
    },
    states: {
      hover: {
        filter: {
          type: "darken",
        },
      },
    },
    tooltip: {
      enabled: true,
      fillSeriesColor: false,
      y: {
        formatter: function (_value: number, opts: any) {
          const section = sortedData[opts.seriesIndex];
          const label =
            metric === "revenue"
              ? formatPrice(section.revenue / 100, currency)
              : `${section.orders} article${section.orders > 1 ? "s" : ""} vendu${section.orders > 1 ? "s" : ""}`;
          return `${label} (${section.share.toFixed(1)}%)`;
        },
      },
    },
    responsive: [
      {
        breakpoint: 1280,
        options: {
          chart: {
            width: 300,
          },
        },
      },
    ],
  };

  return (
    <div
      className="rounded-lg"
      style={{
        backgroundColor: dashboardColors.bgCard,
        padding: dashboardSpacing.cardPadding,
        boxShadow: dashboardShadows.card,
      }}
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3
          className="text-lg font-semibold"
          style={{ color: dashboardColors.textPrimary }}
        >
          Répartition par section
        </h3>
        <div
          role="group"
          aria-label="Répartir par"
          className="flex shrink-0 rounded-lg border border-border p-0.5"
        >
          {(
            [
              { key: "orders", label: "Quantité" },
              { key: "revenue", label: "Revenus" },
            ] as const
          ).map(({ key, label }) => (
            <button
              key={key}
              type="button"
              aria-pressed={metric === key}
              onClick={() => setMetric(key)}
              className={cn(
                "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                metric === key
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className={gridClassName}>
        {/* Pie Chart */}
        <div className="flex items-center justify-center">
          <div id="sectionBreakdownChart">
            {/* key forces a clean remount when the metric changes —
                react-apexcharts leaves the pie stale on same-length series
                updates otherwise. */}
            <ReactApexChart
              key={metric}
              options={options}
              series={series}
              type="pie"
              height={height}
            />
          </div>
        </div>

        {/* Legend and details */}
        <div className="space-y-2">
          <p className="text-sm text-gray-600 mb-3">{legendTitle}</p>
          {sortedData.slice(0, 7).map((section, index) => {
            const previousSection = previousData?.find(
              (p) => p.section.toLowerCase() === section.section.toLowerCase()
            );

            return (
              <div
                key={index}
                className="flex items-center justify-between p-2 rounded hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      backgroundColor:
                        chartColorPalette[index % chartColorPalette.length],
                    }}
                  />
                  <span className="text-sm font-medium">
                    {section.section}
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">{formatMetric(section)}</p>
                  <div className="flex items-center justify-end gap-2">
                    <p className="text-xs text-gray-500">
                      {section.share.toFixed(1)}%
                    </p>
                    {/* Growth reflects the selected metric — reuse the
                        orders-based indicator by feeding it the active value. */}
                    {getGrowthIndicator(
                      { orders: section[metric] },
                      previousSection
                        ? { orders: previousSection[metric] }
                        : undefined,
                      { iconSize: "small", textSize: "xs" }
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
